// src/components/auth/LogoutButton.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/Button/Button';

/**
 * LogoutButton component with confirmation dialog
 * Provides secure logout functionality with user confirmation
 */
export const LogoutButton = ({ 
  variant = 'ghost', 
  size = 'sm', 
  showIcon = true, 
  showText = true,
  confirmLogout = true,
  className = '',
  onLogoutStart,
  onLogoutComplete,
  onLogoutError
}) => {
  const { logout, isLoading, userDisplayName } = useAuth();
  const navigate = useNavigate();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogoutClick = () => {
    if (confirmLogout) {
      setShowConfirmDialog(true);
    } else {
      handleLogout();
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      setShowConfirmDialog(false);
      
      // Call optional callback
      if (onLogoutStart) {
        onLogoutStart();
      }

      // Perform logout
      const result = await logout();
      
      if (result.success) {
        // Call optional callback
        if (onLogoutComplete) {
          onLogoutComplete();
        }
        
        // Navigate to login page
        navigate('/login', { replace: true });
      } else {
        throw new Error(result.error || 'Logout failed');
      }
    } catch (error) {
      console.error('Logout error:', error);
      
      // Call optional error callback
      if (onLogoutError) {
        onLogoutError(error);
      }
      
      // Even if logout fails, redirect to login for security
      navigate('/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleCancelLogout = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      {/* Logout Button */}
      <Button
        variant={variant}
        size={size}
        onClick={handleLogoutClick}
        disabled={isLoading || isLoggingOut}
        className={`${className} ${isLoggingOut ? 'opacity-50' : ''}`}
        title="Sign out"
      >
        {isLoggingOut ? (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
            {showText && 'Signing out...'}
          </div>
        ) : (
          <div className="flex items-center">
            {showIcon && <LogOut className="h-4 w-4" />}
            {showText && showIcon && <span className="ml-2">Sign out</span>}
            {showText && !showIcon && 'Sign out'}
          </div>
        )}
      </Button>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Confirm Sign Out
                </h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Are you sure you want to sign out{userDisplayName ? ` as ${userDisplayName}` : ''}? 
                You will need to sign in again to access your account.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelLogout}
                disabled={isLoggingOut}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
              >
                {isLoggingOut ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Signing out...
                  </div>
                ) : (
                  'Sign out'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Simple logout button without confirmation dialog
 * For use in contexts where immediate logout is preferred
 */
export const QuickLogoutButton = (props) => {
  return <LogoutButton {...props} confirmLogout={false} />;
};

/**
 * Logout menu item for dropdown menus
 * Styled specifically for use in navigation menus
 */
export const LogoutMenuItem = ({ onClick, className = '' }) => {
  const { logout, isLoading } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login', { replace: true });
    }
    
    if (onClick) {
      onClick();
    }
  };

  return (
    <button
      onClick={handleLogout}
      disabled={isLoading}
      className={`
        w-full text-left px-4 py-2 text-sm dropdown-menu-item
        flex items-center disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors duration-150
        ${className}
      `}
      style={{ color: 'var(--dropdown-text-secondary)' }}
    >
      <LogOut className="h-4 w-4 mr-3" />
      {isLoading ? 'Signing out...' : 'Sign out'}
    </button>
  );
};

export default LogoutButton;
